import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CloseIcon from '@mui/icons-material/Close';

/** Customer `Gallery` preview layout. */
export const GALLERY_PREVIEW_MODE = {
  POPUP: 'popup',
  /** Large preview with arrows + thumbnail strip (when multiple images). */
  FIXED: 'fixed',
  /** Like POPUP (click opens lightbox) but one wide thumbnail with fixed-style L/R arrows. */
  SINGLE_THUMB: 'single-thumb',
};

const pictureSizeMap = {
  small: { thumbW: 80, thumbH: 60, thumbWMobile: 64, thumbHMobile: 48 },
  medium: { thumbW: 112, thumbH: 84, thumbWMobile: 88, thumbHMobile: 66 },
  large: { thumbW: 144, thumbH: 108, thumbWMobile: 112, thumbHMobile: 84 },
};

/**
 * Photo gallery with configurable preview mode.
 * @param {string[]} pictureUrls - Array of image URLs to display.
 * @param {'popup'|'fixed'|'single-thumb'} previewMode - Use {@link GALLERY_PREVIEW_MODE} (default: POPUP).
 * @param {'small'|'medium'|'large'} [size='medium'] - Thumbnail and picture size.
 * @param {string} [previewAspectRatio] - For {@link GALLERY_PREVIEW_MODE.SINGLE_THUMB}: use this CSS aspect-ratio (e.g. `'16/9'`) instead of fixed thumbnail height.
 */
const Gallery = ({
  pictureUrls,
  previewMode = GALLERY_PREVIEW_MODE.POPUP,
  size = 'medium',
  previewAspectRatio,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isPopupMode = previewMode === GALLERY_PREVIEW_MODE.POPUP;
  const isSingleThumb = previewMode === GALLERY_PREVIEW_MODE.SINGLE_THUMB;
  const usesLightbox = isPopupMode || isSingleThumb;
  /** FIXED mode: thumbnail grid below the large preview (when multiple images). */
  const showFixedThumbnailStrip =
    !isPopupMode && !isSingleThumb && pictureUrls.length > 1;
  const pictureSize = pictureSizeMap[size] || pictureSizeMap.medium;
  const thumbW = isMobile ? pictureSize.thumbWMobile : pictureSize.thumbW;
  const thumbH = isMobile ? pictureSize.thumbHMobile : pictureSize.thumbH;

  const goPrevious = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, []);
  const goNext = useCallback(() => {
    setCurrentIndex((i) => Math.min(pictureUrls.length - 1, i + 1));
  }, [pictureUrls.length]);

  const openPopup = useCallback((index) => {
    setCurrentIndex(index);
    setPopupOpen(true);
  }, []);
  const closePopup = useCallback(() => setPopupOpen(false), []);

  useEffect(() => {
    if (!Array.isArray(pictureUrls) || pictureUrls.length <= 1) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
      if (usesLightbox && e.key === 'Escape') {
        closePopup();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pictureUrls, goPrevious, goNext, usesLightbox, closePopup]);

  const emptyPlaceholderAspectRatio =
    isSingleThumb && previewAspectRatio ? previewAspectRatio : '4/3';

  if (!Array.isArray(pictureUrls) || pictureUrls.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          aspectRatio: emptyPlaceholderAspectRatio,
          backgroundColor: 'grey.300',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Brak zdjęć
        </Typography>
      </Box>
    );
  }

  const current = pictureUrls[currentIndex];
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < pictureUrls.length - 1;
  const captionText = `${currentIndex + 1} / ${pictureUrls.length}`;

  const pictureCaption = (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      {captionText}
    </Typography>
  );

  const thumbnails = (
    <Box
      sx={{
        mt: isPopupMode ? 0 : 1.5,
        width: '100%',
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${thumbW}px, 1fr))`,
        gap: 1,
      }}
    >
      {pictureUrls.map((url, i) => (
        <Box
          key={i}
          component="button"
          onClick={() => (isPopupMode ? openPopup(i) : setCurrentIndex(i))}
          sx={{
            width: '100%',
            height: thumbH,
            borderRadius: 1,
            overflow: 'hidden',
            border: 2,
            borderColor: !isPopupMode && i === currentIndex ? 'primary.main' : 'transparent',
            padding: 0,
            cursor: 'pointer',
            backgroundColor: 'grey.200',
            '&:hover': { opacity: 0.9 },
          }}
        >
          <Box
            component="img"
            src={url}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
      ))}
    </Box>
  );

  const fixedStyleOverlayArrowsSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255,255,255,0.8)',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
    '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.4)' },
  };

  const singleThumbStrip = (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        ...(previewAspectRatio
          ? { aspectRatio: previewAspectRatio }
          : { height: thumbH }),
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: 'grey.200',
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => openPopup(currentIndex)}
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          p: 0,
          border: 'none',
          cursor: 'pointer',
          backgroundColor: 'transparent',
        }}
      >
        <Box
          component="img"
          src={current}
          alt=""
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            pointerEvents: 'none',
          }}
        />
      </Box>
      {pictureUrls.length > 1 && (
        <>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              goPrevious();
            }}
            disabled={!hasPrevious}
            sx={{ ...fixedStyleOverlayArrowsSx, left: 8, zIndex: 1 }}
            size="large"
            aria-label="Poprzednie zdjęcie"
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            disabled={!hasNext}
            sx={{ ...fixedStyleOverlayArrowsSx, right: 8, zIndex: 1 }}
            size="large"
            aria-label="Następne zdjęcie"
          >
            <NavigateNextIcon />
          </IconButton>
        </>
      )}
    </Box>
  );

  const lightboxDialog = (
    <Dialog
      open={popupOpen}
      onClose={closePopup}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: '90vw',
          maxHeight: '90vh',
          backgroundColor: 'rgba(0,0,0,0.9)',
          boxShadow: 24,
        },
      }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.85)' } }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: 280,
        }}
      >
        <IconButton
          onClick={closePopup}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            zIndex: 1,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
          }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <Box
            component="img"
            src={current}
            alt=""
            sx={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
            }}
          />
          {pictureUrls.length > 1 && (
            <Typography variant="body2" sx={{ color: 'white', mt: 1 }}>
              {captionText}
            </Typography>
          )}
        </Box>
        {pictureUrls.length > 1 && (
          <>
            <IconButton
              onClick={goPrevious}
              disabled={!hasPrevious}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.4)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)', backgroundColor: 'transparent' },
              }}
              size="large"
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              onClick={goNext}
              disabled={!hasNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.4)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)', backgroundColor: 'transparent' },
              }}
              size="large"
            >
              <NavigateNextIcon />
            </IconButton>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {isPopupMode && (
        <>
          {thumbnails}
          {pictureUrls.length > 1 && pictureCaption}
        </>
      )}
      {isSingleThumb && (
        <>
          {singleThumbStrip}
          {pictureUrls.length > 1 && pictureCaption}
        </>
      )}
      {usesLightbox && lightboxDialog}
      {!isPopupMode && !isSingleThumb && (
        <>
          {/* FIXED: large preview + optional thumbnail grid */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4/3',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: 'grey.200',
            }}
          >
            <Box
              component="img"
              src={current}
              alt=""
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            {pictureUrls.length > 1 && (
              <>
                <IconButton
                  onClick={goPrevious}
                  disabled={!hasPrevious}
                  sx={{ ...fixedStyleOverlayArrowsSx, left: 8 }}
                  size="large"
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton
                  onClick={goNext}
                  disabled={!hasNext}
                  sx={{ ...fixedStyleOverlayArrowsSx, right: 8 }}
                  size="large"
                >
                  <NavigateNextIcon />
                </IconButton>
              </>
            )}
          </Box>
          {pictureUrls.length > 1 && pictureCaption}
          {showFixedThumbnailStrip && thumbnails}
        </>
      )}
    </Box>
  );
};

export default Gallery;
